import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from 'app/AppStore';
import { fetchSearchRateLimitViolationsRequest } from 'domains/searchRateLimit/SearchRateLimit.store';
import {
  SearchRateLimitViolation,
  SearchRateLimitViolationMetrics,
} from 'domains/searchRateLimit/SearchRateLimit.types';
import { SectionWrapper } from 'pages/SettingsPage/General/GeneralSettings.styled';
import { Flex, Spin, Text } from 'UI';

// Container for metrics card with badge in top-right
const MetricsCard = styled.div`
  position: relative;
`;

interface RateLimitMetricsProps {
  countryCode: string;
}

const MAX_RECENT_VIOLATIONS = 5;

const EMPTY_METRICS: SearchRateLimitViolationMetrics = {
  todayCount: 0,
  weekCount: 0,
  topViolator: null,
};

interface ViolationSectionProps {
  title: string;
  subtitle: string;
  violations: SearchRateLimitViolation[];
  metrics: SearchRateLimitViolationMetrics;
}

const ViolationSection: React.FC<ViolationSectionProps> = ({ title, subtitle, violations, metrics }) => {
  const { t } = useTranslation();
  const topViolator = metrics.topViolator || '—';

  const metricCards = [
    {
      value: metrics.todayCount,
      label: t('backoffice.search-rate-limit.metrics.today', "Today's violations"),
      dataCy: 'metric-today',
    },
    {
      value: metrics.weekCount,
      label: t('backoffice.search-rate-limit.metrics.week', 'This week'),
      dataCy: 'metric-week',
    },
    {
      value: topViolator,
      label: t('backoffice.search-rate-limit.metrics.top-violator', 'Top violator'),
      dataCy: 'metric-top-violator',
      title: topViolator,
    },
  ];

  return (
    <SectionWrapper $marginY={0}>
      <Flex direction={'column'} gap={10}>
        <Text type="heading4">{title}</Text>
        <Text type="base">{subtitle}</Text>

        <Flex gap={15} wrap={'wrap'}>
          {metricCards.map(({ value, label, dataCy, title: metricTitle }) => (
            <SectionWrapper key={`${title}-${dataCy}`} data-cy={`${title}-${dataCy}`} $marginY={0}>
              <Flex direction={'column'} align={'center'} gap={5}>
                <div title={metricTitle}>
                  <Text type="heading3">{String(value)}</Text>
                </div>
                <Text type="base">{label}</Text>
              </Flex>
            </SectionWrapper>
          ))}
        </Flex>

        <Flex direction={'column'} gap={8} data-cy={`${title}-recent-violations`}>
          <Text type={'h4'}>{t('backoffice.search-rate-limit.violations.recent', 'Recent Violations')}</Text>
          {violations.length === 0 ? (
            <Text type="base">—</Text>
          ) : (
            <>
              {violations.slice(0, MAX_RECENT_VIOLATIONS).map((violation) => (
                <SectionWrapper
                  key={`${title}-${violation.timestamp}-${violation.garageId}-${violation.ipn}`}
                  $marginY={0}
                >
                  <Flex justify={'space-between'} gap={10} wrap={'wrap'}>
                    <Text type={'text'}>
                      {t('backoffice.search-rate-limit.violations.timestamp', 'Timestamp')}:{' '}
                      {violation.timestamp ? new Date(violation.timestamp).toLocaleString() : '—'}
                    </Text>
                    <Text type={'text'}>
                      {t('backoffice.search-rate-limit.violations.ipn', 'IPN')}: {violation.ipn || '—'}
                    </Text>
                    <Text type={'text'}>
                      {t('backoffice.search-rate-limit.violations.garage-id', 'Garage ID')}: {violation.garageId || '—'}
                    </Text>
                  </Flex>
                </SectionWrapper>
              ))}
              {violations.length > MAX_RECENT_VIOLATIONS && (
                <Text type="base">
                  {t('backoffice.search-rate-limit.violations.more', '... and {{count}} more violations', {
                    count: violations.length - MAX_RECENT_VIOLATIONS,
                  })}
                </Text>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </SectionWrapper>
  );
};

export const RateLimitMetrics: React.FC<RateLimitMetricsProps> = ({ countryCode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const violations = useSelector((state: RootState) => state.searchRateLimit.violations);
  const loading = useSelector((state: RootState) => state.searchRateLimit.violationsLoading);
  const shortTermMetrics = useSelector((state: RootState) => state.searchRateLimit.shortTermMetrics) ?? EMPTY_METRICS;
  const longTermMetrics = useSelector((state: RootState) => state.searchRateLimit.longTermMetrics) ?? EMPTY_METRICS;
  const totalViolationCount = useSelector((state: RootState) => state.searchRateLimit.totalViolationCount);

  useEffect(() => {
    dispatch(fetchSearchRateLimitViolationsRequest(countryCode));
  }, [dispatch, countryCode]);

  const shortTermViolations = violations.filter((v) => v.limitType === 'SHORT_TERM');
  const longTermViolations = violations.filter((v) => v.limitType === 'LONG_TERM');

  if (loading) {
    return (
      <Flex justify={'center'}>
        <Spin />
      </Flex>
    );
  }

  return (
    <MetricsCard>
      <Flex direction={'column'} gap={10}>
        <SectionWrapper key={`rate-limit-metrics-total`} data-cy={`rate-limit-metrics-total`} $marginY={0}>
          <Flex direction={'column'} align={'center'} gap={5}>
            <div title={t('backoffice.search-rate-limit.metrics.total', 'Total')}>
              <Text type="heading3">{String(totalViolationCount)}</Text>
            </div>
            <Text type="base">{t('backoffice.search-rate-limit.metrics.total', 'Total')}</Text>
          </Flex>
        </SectionWrapper>

        <ViolationSection
          title={t('backoffice.search-rate-limit.violations.short-term-title', 'Short-Term Violations')}
          subtitle={t(
            'backoffice.search-rate-limit.violations.short-term-overview',
            'Violations triggered by the short-term search limit window.',
          )}
          violations={shortTermViolations}
          metrics={shortTermMetrics}
        />

        <ViolationSection
          title={t('backoffice.search-rate-limit.violations.long-term-title', 'Long-Term Violations')}
          subtitle={t(
            'backoffice.search-rate-limit.violations.long-term-overview',
            'Violations triggered by the long-term search limit window.',
          )}
          violations={longTermViolations}
          metrics={longTermMetrics}
        />
      </Flex>
    </MetricsCard>
  );
};
